import React from "react";
import { AlertTriangle, Check, RefreshCw } from "lucide-react";

interface Props {
  isOpen: boolean;
  selectedTimezone: string;
  detectedTimezone: string;
  onKeepSelected: () => void;      // User chose: keep what they picked
  onUseDetected: () => void;       // User chose: revert to browser-detected
}

export const TimezoneMismatchModal: React.FC<Props> = ({
  isOpen,
  selectedTimezone,
  detectedTimezone,
  onKeepSelected,
  onUseDetected,
}) => {
  if (!isOpen) return null;

  return (
    <div
      className="tz-modal-overlay fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm animate-in fade-in duration-200"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tz-modal-title"
    >
      <div className="tz-modal-card w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-800 dark:bg-slate-900 animate-in zoom-in-95 duration-200">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-600 dark:bg-amber-950/50 dark:text-amber-400">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div>
            <h2 id="tz-modal-title" className="text-lg font-bold text-slate-900 dark:text-white">
              Timezone Mismatch Detected
            </h2>
            <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
              You selected <strong>{selectedTimezone}</strong>, but your browser
              reports you are currently in <strong>{detectedTimezone}</strong>.
            </p>
            <p className="tz-modal-hint mt-2 text-xs text-slate-500 dark:text-slate-400">
              Choose whichever matches your <em>business operations</em> region,
              not necessarily your physical location.
            </p>
          </div>
        </div>

        <div className="tz-modal-actions mt-6 flex flex-col sm:flex-row gap-3">
          <button
            id="tz-modal-keep-selected"
            type="button"
            className="btn btn-primary flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            onClick={onKeepSelected}
          >
            <Check className="h-4 w-4" />
            Keep Selected: {selectedTimezone}
          </button>
          <button
            id="tz-modal-use-detected"
            type="button"
            className="btn btn-secondary flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-700 shadow-sm hover:bg-slate-50 transition-colors dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
            onClick={onUseDetected}
          >
            <RefreshCw className="h-4 w-4" />
            Use Detected: {detectedTimezone}
          </button>
        </div>
      </div>
    </div>
  );
};

export default TimezoneMismatchModal;